import React from "react";
import { Link } from "react-router-dom";
import { Mail, MessageSquare, BookOpen, Download } from "lucide-react";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import UtmBanner from "@/components/landing/UtmBanner";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import Faq from "@/components/landing/Faq";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import { SEO } from "@/data/seo";

const FAQ_ITEMS = [
  {
    q: "Wat kost EquiSight?",
    a: "Basic is gratis (€0) voor 1 paard. Premium €4,99/maand (meerdere paarden + manege + cloud backup). Pro €14,99/maand (Trainer + Fokkerij). Professional €24,99/maand (Hoefsmid + Tandarts + Fysio + agenda + klantbeheer).",
  },
  {
    q: "Werkt EquiSight op iPhone én Android?",
    a: "Ja. EquiSight werkt op iOS en Android. De Bèta start op 1 september 2026.",
  },
  {
    q: "Hoe veilig is mijn data?",
    a: "Alle data wordt versleuteld opgeslagen binnen de EU. Voor vaccinatie- en medische data ben jij eigenaar. Lees onze privacyverklaring voor details.",
  },
  {
    q: "Hoe word ik Bèta tester?",
    a: "Schrijf je in op de wachtlijst via de homepage. Bèta testers krijgen 1 maand Professional gratis bij launch.",
  },
  {
    q: "Hoe zeg ik op?",
    a: "Maandelijks opzegbaar. Je kan een betaald abonnement op ieder moment terugzetten naar Basic (gratis) in de app.",
  },
  {
    q: "Kan ik EquiSight gebruiken zonder eigen paard?",
    a: "Ja. Met de Manege functionaliteit (inbegrepen in Premium €4,99/maand) volg je trainingen op manegepaarden.",
  },
];

export const Support = () => {
  const breadcrumbs = [{ name: "Support", path: "/support" }];
  return (
    <main data-testid="page-support" className="bg-background">
      <Seo {...SEO.support} path="/support" breadcrumbs={breadcrumbs} faq={FAQ_ITEMS.slice(0, 3)} />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs items={breadcrumbs} />

      <section className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">Support</p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] sm:text-5xl lg:text-6xl">
          We helpen je snel op weg.
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[hsl(var(--ink-soft))]">
          Heb je een vraag of loop je ergens vast? Stuur ons een mail of bekijk de veelgestelde
          vragen.
        </p>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <a
            href="mailto:support@equisight.nl"
            data-testid="support-email"
            className="flex items-start gap-4 rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-7 transition-all hover:border-[hsl(var(--forest))]"
          >
            <Mail className="h-6 w-6 text-[hsl(var(--forest))]" strokeWidth={1.5} />
            <div>
              <p className="font-display text-xl text-[hsl(var(--ink))]">Mail ons</p>
              <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">support@equisight.nl</p>
              <p className="mt-2 text-xs text-[hsl(var(--ink-soft))]">Antwoord binnen 24 uur op werkdagen</p>
            </div>
          </a>
          <Link
            to="/faq"
            data-testid="support-faq"
            className="flex items-start gap-4 rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-7 transition-all hover:border-[hsl(var(--forest))]"
          >
            <MessageSquare className="h-6 w-6 text-[hsl(var(--forest))]" strokeWidth={1.5} />
            <div>
              <p className="font-display text-xl text-[hsl(var(--ink))]">Veelgestelde vragen</p>
              <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">FAQ — over prijzen, account en privacy</p>
            </div>
          </Link>
          <Link
            to="/kennisbank"
            data-testid="support-kennisbank"
            className="flex items-start gap-4 rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-7 transition-all hover:border-[hsl(var(--forest))]"
          >
            <BookOpen className="h-6 w-6 text-[hsl(var(--forest))]" strokeWidth={1.5} />
            <div>
              <p className="font-display text-xl text-[hsl(var(--ink))]">Kennisbank</p>
              <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">Artikelen over gezondheid, voeding en training</p>
            </div>
          </Link>
          <Link
            to="/downloads"
            data-testid="support-downloads"
            className="flex items-start gap-4 rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-7 transition-all hover:border-[hsl(var(--forest))]"
          >
            <Download className="h-6 w-6 text-[hsl(var(--forest))]" strokeWidth={1.5} />
            <div>
              <p className="font-display text-xl text-[hsl(var(--ink))]">Downloads</p>
              <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">Gratis checklists en templates</p>
            </div>
          </Link>
        </div>
      </section>

      <Faq items={FAQ_ITEMS} eyebrow="FAQ" title="Veelgestelde vragen" />

      <FinalCTA />
      <Footer />
    </main>
  );
};

export const FaqPage = () => {
  const breadcrumbs = [{ name: "FAQ", path: "/faq" }];
  return (
    <main data-testid="page-faq" className="bg-background">
      <Seo {...SEO.faq} path="/faq" breadcrumbs={breadcrumbs} faq={FAQ_ITEMS} />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs items={breadcrumbs} />
      <section className="mx-auto max-w-3xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">FAQ</p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] sm:text-5xl">
          Veelgestelde vragen.
        </h1>
        <p className="mt-6 text-lg text-[hsl(var(--ink-soft))]">
          Alles wat je wilt weten over EquiSight — prijzen, account, privacy, gebruik en meer.
        </p>
      </section>
      <Faq items={FAQ_ITEMS} eyebrow="Antwoorden" title="Onze antwoorden" />
      <FinalCTA />
      <Footer />
    </main>
  );
};

export const Downloads = () => {
  const breadcrumbs = [{ name: "Downloads", path: "/downloads" }];
  return (
    <main data-testid="page-downloads" className="bg-background">
      <Seo {...SEO.downloads} path="/downloads" breadcrumbs={breadcrumbs} />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs items={breadcrumbs} />
      <section className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">Downloads</p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] sm:text-5xl lg:text-6xl">
          Gratis checklists & templates.
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[hsl(var(--ink-soft))]">
          Praktische downloads voor iedere paardeneigenaar — direct printbaar of in te vullen.
          Beschikbaar bij launch van EquiSight op 1 september 2026.
        </p>

        <ul className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            "Noodnummers checklist paard",
            "Voederplan template",
            "Ontwormingsschema (EPG-based)",
            "Vaccinatiekalender paard",
            "Hoefsmid-log template",
            "Pre-wedstrijd checklist",
          ].map((d) => (
            <li
              key={d}
              className="flex items-center justify-between rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6"
            >
              <span className="text-[hsl(var(--ink))]">{d}</span>
              <span className="rounded-full bg-[hsl(var(--sand))]/15 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
                Binnenkort
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-12 text-sm text-[hsl(var(--ink-soft))]">
          Wil je als eerste deze downloads krijgen?{" "}
          <Link to="/#waitlist" className="border-b border-[hsl(var(--ink))]/30 hover:border-[hsl(var(--ink))]">
            Schrijf je in op de wachtlijst.
          </Link>
        </p>
      </section>
      <FinalCTA />
      <Footer />
    </main>
  );
};

export const Blog = () => {
  const breadcrumbs = [{ name: "Blog", path: "/blog" }];
  return (
    <main data-testid="page-blog" className="bg-background">
      <Seo {...SEO.blog} path="/blog" breadcrumbs={breadcrumbs} />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs items={breadcrumbs} />
      <section className="mx-auto max-w-3xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">Blog</p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] sm:text-5xl lg:text-6xl">
          Paardenkennis & EquiSight nieuws.
        </h1>
        <p className="mt-6 text-lg text-[hsl(var(--ink-soft))]">
          De blog start op 1 september 2026 — gelijktijdig met de Bèta launch. Verwacht artikelen over
          paardenbeheer, gezondheid, training en productupdates.
        </p>
        <div className="mt-12">
          <Link
            to="/kennisbank"
            className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--ink))] px-6 py-3 text-sm font-medium text-[hsl(var(--ink))] transition-all hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--background))]"
          >
            Bekijk Kennisbank →
          </Link>
        </div>
      </section>
      <FinalCTA />
      <Footer />
    </main>
  );
};

export const Vergelijkingen = () => {
  const breadcrumbs = [{ name: "Vergelijkingen", path: "/vergelijkingen" }];
  const items = [
    {
      name: "EquiSight vs Excel",
      lead: "Waarom een paardendossier in Excel je beperkt — en wat een app uniek maakt.",
    },
    {
      name: "EquiSight vs Notitieboekjes",
      lead: "Het einde van de schoenendoos. Eén digitaal dossier dat altijd met je meereist.",
    },
    {
      name: "EquiSight vs WhatsApp groepen",
      lead: "Communicatie met je trainer, hoefsmid en dierenarts — zonder het verspreid in chats.",
    },
  ];
  return (
    <main data-testid="page-vergelijkingen" className="bg-background">
      <Seo {...SEO.vergelijkingen} path="/vergelijkingen" breadcrumbs={breadcrumbs} />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs items={breadcrumbs} />
      <section className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">Vergelijkingen</p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] sm:text-5xl lg:text-6xl">
          EquiSight versus alternatieven.
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[hsl(var(--ink-soft))]">
          Hoe verhoudt EquiSight zich tot andere oplossingen? Eerlijke vergelijkingen volgen bij
          launch.
        </p>
        <ul className="mt-12 space-y-4">
          {items.map((it) => (
            <li
              key={it.name}
              className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6"
            >
              <p className="font-display text-2xl text-[hsl(var(--ink))]">{it.name}</p>
              <p className="mt-2 text-[hsl(var(--ink-soft))]">{it.lead}</p>
              <span className="mt-3 inline-block rounded-full bg-[hsl(var(--sand))]/15 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
                Binnenkort
              </span>
            </li>
          ))}
        </ul>
      </section>
      <FinalCTA />
      <Footer />
    </main>
  );
};
